// An improvement or implementation request as one lean A3 card: six boxes in story order (Problem, Target, Cause, Fix,
// To do, Result) and three steps (Plan, Do, Check). Plan: write boxes 1 to 5 (they save as you type), then a person
// approves the plan. Do: people tick their tasks and give an OK on each change an AI made; the last OK puts it live.
// Check: enter the result at 30 days, then keep it (the new numbers go into the process catalog) or roll it back.
// Product changes to CourageMFG and changes to an app keep their own page (Factory.tsx).
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CommandBar } from "../Shell";
import { ErrorBox } from "../ui";
import { SUITE } from "../suite";
import { NumberCard } from "../analytics/NumberCard";
import { factory, CYCLE, STATE_LABEL, stepIndex, usd, val, when, type RequestDetail, type Task } from "./factoryApi";
import { ProcessPicker, settleProcess, type ProcessValue } from "../improve/ProcessPicker";
import "./factory.css";

const areaName = (id: string | null) => (id ? SUITE.find((d) => d.id === id)?.name ?? id : null);
type Act = (fn: () => Promise<unknown>, done?: string) => Promise<boolean>;
const PLAN = ["roadmap", "define", "design"];
const DO = ["build", "test", "deploy"];

/** The three steps. On the roadmap, none has started; closed, all are done. */
function Steps({ state }: { state: string }) {
  const at = state === "closed" ? CYCLE.length : stepIndex(state);
  return (
    <div className="bpf a3-steps" role="list" aria-label="Plan, Do, Check">
      {CYCLE.map((s, i) => (
        <div key={s.id} role="listitem" className={`bpf-stage ${i < at ? "done" : i === at ? "current" : ""}`} aria-current={i === at ? "step" : undefined}>
          <span className="name">{s.name}</span>
        </div>
      ))}
    </div>
  );
}

/** What the plan still needs before a person can approve it. */
function planMissing(d: RequestDetail) {
  return [d.projectType === "improvement" && !d.process && "the process", !d.problem && "the problem", (!d.measure || d.currentValue === null) && "where it is now", d.targetValue === null && "a target", !d.cause && "the cause",
    !d.designs.length && "the fix", !d.tasks.some((t) => t.state !== "cancelled") && "a task"].filter(Boolean) as string[];
}

export function LeanRequest({ d, act, note, err, reload }: { d: RequestDetail; act: Act; note: string | null; err: unknown; reload: () => void }) {
  const ask = (q: string) => { const v = prompt(q); return v && v.trim() ? v.trim() : null; };
  const missing = planMissing(d);
  const inPlan = ["define", "design"].includes(d.state);
  const open = ![ "closed", "cancelled"].includes(d.state);
  return (
    <>
      <CommandBar status={<>{d.number} · {STATE_LABEL[d.state] ?? d.state}</>}>
        {d.state === "roadmap" && <button className="cmd primary" onClick={() => act(() => factory.start(d.number), "Started. Write the plan: the problem, the target, the cause, the fix and the tasks.")}>Start</button>}
        {inPlan && <button className="cmd primary" disabled={missing.length > 0} title={missing.length ? `First add ${missing.join(", ")}` : undefined}
          onClick={() => act(() => factory.approvePlan(d.number), "Plan approved. The tasks are ready.")}>Approve the plan</button>}
        {d.state === "deploy" && <button className="cmd primary" onClick={() => act(() => factory.deploy(d.number), "It's live. Enter the result in 30 days.")}>Put it live</button>}
        {DO.includes(d.state) && <button className="cmd" onClick={() => { const why = ask("Why must the plan change?"); if (why) act(() => factory.reopenDesign(d.number, why), "Back to Plan. Unfinished tasks are back to draft."); }}>Change the plan</button>}
        {open && d.state !== "measure" && d.state !== "recatalog" && <button className="cmd" onClick={() => { const why = ask("Why cancel this request?"); if (why) act(() => factory.cancel(d.number, why), "Cancelled."); }}>Cancel request</button>}
      </CommandBar>
      <div className="page">
        <div className="page-title"><h1>{d.title}</h1>
          <span className="sub">{[d.number, d.projectTypeLabel, areaName(d.area), d.valuePerYear !== null ? `${usd(d.valuePerYear)} a year` : null].filter(Boolean).join(" · ")}</span>
        </div>
        <Steps state={d.state} />
        {d.state === "roadmap" && <div className="msg info">On {areaName(d.area) ? `${areaName(d.area)}'s` : "the"} <Link to="/improve/roadmap">roadmap</Link>. Start it when the department is ready, then write the plan.</div>}
        {inPlan && missing.length > 0 && <div className="msg info">To approve the plan, add {missing.join(", ")}.</div>}
        {d.state === "deploy" && <div className="msg info">Every task is done. Put it live.</div>}
        <ErrorBox error={err} />
        {note && <div className="msg info">{note}</div>}
        <A3 d={d} act={act} reload={reload} />
        {d.state === "measure" && <NumberCard record={d.number} onRecordResult={async (value) => { await act(() => factory.result(d.number, { value, note: "From the dashboard number" }), "Result recorded from the dashboard."); }} />}
        <History d={d} />
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// The card
// ---------------------------------------------------------------------------------------------------------------
type Text = { problem: string; cause: string; measure: string; unit: string; currentValue: string; targetValue: string };
const textOf = (d: RequestDetail): Text => ({ problem: d.problem, cause: d.cause, measure: d.measure ?? "", unit: d.unit ?? "", currentValue: d.currentValue?.toString() ?? "", targetValue: d.targetValue?.toString() ?? "" });

function A3({ d, act, reload }: { d: RequestDetail; act: Act; reload: () => void }) {
  const editable = PLAN.includes(d.state);
  const [t, setT] = useState<Text>(() => textOf(d));
  const [fix, setFix] = useState(d.designs[0]?.body ?? "");
  const [status, setStatus] = useState<{ text: string; bad?: boolean }>({ text: "" });
  const saved = useRef<Text>(t);
  const timer = useRef<number | null>(null);
  // A new request, or back to Plan: start from what's saved.
  useEffect(() => { const x = textOf(d); setT(x); saved.current = x; setFix(d.designs[0]?.body ?? ""); }, [d.number, d.state]);
  const fromCatalog = !!d.insight || (!!d.process && d.unit === "minutes");
  const num = (s: string) => (s.trim() === "" ? null : Number(s));
  const change = (k: keyof Text, v: string) => {
    const next = { ...t, [k]: v };
    setT(next);
    if (timer.current) window.clearTimeout(timer.current);
    setStatus({ text: "Saving…" });
    timer.current = window.setTimeout(async () => {
      const diff: Record<string, unknown> = {};
      for (const key of Object.keys(next) as (keyof Text)[]) if (next[key] !== saved.current[key]) diff[key] = key === "currentValue" || key === "targetValue" ? num(next[key]) : next[key];
      if (!Object.keys(diff).length) { setStatus({ text: "All changes saved" }); return; }
      if (("currentValue" in diff && Number.isNaN(diff.currentValue)) || ("targetValue" in diff && Number.isNaN(diff.targetValue))) { setStatus({ text: "Fix the marked number to save it", bad: true }); return; }
      try { await factory.update(d.number, diff); saved.current = { ...saved.current, ...next }; setStatus({ text: "All changes saved" }); reload(); }
      catch (x: any) { setStatus({ text: `Not saved: ${x?.message ?? "the server refused it"}`, bad: true }); }
    }, 700);
  };
  const saveFix = async () => {
    const body = fix.trim();
    if (body === (d.designs[0]?.body ?? "").trim()) return;
    if (body.length < 10) { setStatus({ text: "Not saved: describe the fix in 10 characters or more", bad: true }); return; }
    setStatus({ text: "Saving…" });
    try { await factory.addDesign(d.number, body, d.designs[0]?.acceptance ?? []); setStatus({ text: "All changes saved" }); reload(); }
    catch (x: any) { setStatus({ text: `Not saved: ${x?.message ?? "the server refused it"}`, bad: true }); }
  };
  const unit = d.unit ? ` ${d.unit}` : "";
  return (
    <section className="card a3" aria-label="A3">
      <div className="a3-bar">
        <b>A3</b><span className="muted">{editable ? "Saves as you type" : d.approvedAt ? `Plan approved ${when(d.approvedAt)}` : ""}</span>
        {status.text && <span className={`a3-status ${status.bad ? "bad" : ""}`} role="status">{status.text}</span>}
      </div>
      <ProcessBar d={d} editable={editable} act={act} />
      <div className="a3-grid">
        <div className="a3-col">
          <Box n={1} title="Problem" hint={editable && "What's wrong, who it hurts and what it costs."}>
            {editable ? <textarea className="textarea" aria-label="Problem" rows={3} value={t.problem} onChange={(e) => change("problem", e.target.value)} placeholder="e.g. Supplier NCRs wait 9 days for a disposition. Scrap and rework pile up." />
              : <Para text={d.problem} />}
          </Box>
          <Box n={2} title="Target" hint={editable && (fromCatalog ? (d.insight ? "Now: from process mining." : `Now: from ${d.process!.number} in the process catalog.`) : "Where it is now, and where it should be.")}>
            {editable ? (
              <div className="a3-target">
                {!fromCatalog && <>
                  <label className="a3-f wide"><span>Measure</span><input className="input" value={t.measure} onChange={(e) => change("measure", e.target.value)} placeholder="e.g. Days an NCR waits" /></label>
                  <label className="a3-f"><span>Now</span><input className="input" type="number" step="any" value={t.currentValue} onChange={(e) => change("currentValue", e.target.value)} /></label>
                  <label className="a3-f"><span>Unit</span><input className="input" value={t.unit} onChange={(e) => change("unit", e.target.value)} placeholder="days, %, $" /></label>
                </>}
                {fromCatalog && <div className="a3-now"><span className="l">{d.measure}</span><b>{val(d.currentValue, d.unit)}</b><span className="l">now</span></div>}
                <label className="a3-f"><span>Target{fromCatalog ? unit : ""}</span><input className="input" type="number" step="any" aria-label="Target" value={t.targetValue} onChange={(e) => change("targetValue", e.target.value)} /></label>
              </div>
            ) : (
              <div className="a3-big"><span className="l">{d.measure ?? "No measure"}</span><b>{val(d.currentValue, d.unit)} <span aria-hidden="true">→</span><span className="sr-only">to</span> {val(d.targetValue, d.unit)}</b></div>
            )}
            {d.valuePerYear !== null && <p className="a3-worth">Worth {usd(d.valuePerYear)} a year{d.process && d.unit === "minutes" ? `: the minutes saved × ${d.process.volumePerMonth ?? "–"} a month at the cost an hour` : ""}.</p>}
          </Box>
          <Box n={3} title="Cause" hint={editable && "Why the gap exists. Ask why until you reach something you can change."}>
            {editable ? <textarea className="textarea" aria-label="Cause" rows={3} value={t.cause} onChange={(e) => change("cause", e.target.value)} placeholder="e.g. Supplier NCRs wait until an engineer happens to see them." />
              : <Para text={d.cause} />}
          </Box>
        </div>
        <div className="a3-col">
          <Box n={4} title="Fix" hint={editable && "What changes. Take the waste out first, before adding anything."}>
            {editable ? <textarea className="textarea" aria-label="Fix" rows={4} value={fix} onChange={(e) => setFix(e.target.value)} onBlur={saveFix} placeholder="e.g. Send supplier NCRs straight to the engineer for that supplier. Escalate after 48 hours." />
              : <Para text={d.designs[0]?.body ?? ""} />}
          </Box>
          <Box n={5} title="To do" hint={editable ? "Each task, and who does it. An AI's task needs a person's OK." : undefined}>
            <ToDo d={d} act={act} />
          </Box>
          <Box n={6} title="Result" hint={DO.includes(d.state) || PLAN.includes(d.state) ? "Enter the result 30 days after it goes live, then keep it or roll it back." : undefined}>
            <Result d={d} act={act} />
          </Box>
        </div>
      </div>
    </section>
  );
}

/** The process it improves and the step on its map: shown on every A3, changed while it's at Plan. */
function ProcessBar({ d, editable, act }: { d: RequestDetail; editable: boolean; act: Act }) {
  const [edit, setEdit] = useState(false);
  const [v, setV] = useState<ProcessValue>({ process: d.process?.number ?? "", step: d.processStep?.id ?? "" });
  const p = d.process;
  const n = d.processStep && p ? p.steps.find((s) => s.id === d.processStep!.id)?.n : undefined;
  const save = async (e: FormEvent) => {
    e.preventDefault();
    const processId = await settleProcess(v).catch(() => undefined);
    if (await act(() => factory.update(d.number, { processId: processId ?? null, step: v.step || null }), "Saved.")) setEdit(false);
  };
  if (edit) return (
    <form className="a3-proc edit" onSubmit={save}>
      <ProcessPicker value={v} onChange={setV} required={d.projectType === "improvement"} allowNew idPrefix="a3p" area={d.area} />
      <div className="btn-row"><button className="btn primary">Save</button><button type="button" className="btn" onClick={() => setEdit(false)}>Cancel</button></div>
    </form>
  );
  return (
    <div className="a3-proc">
      <span className="l">Process</span>
      {p ? <><Link to={`/improve/catalog/${p.number}`}>{p.number} {p.name}</Link>
        {d.processStep ? <span>· {p.mapId ? <Link to={`/processes/${p.mapId}`}>Step{n ? ` ${n}` : ""}: {d.processStep.name}</Link> : `Step: ${d.processStep.name}`}</span> : <span className="muted">· the whole process</span>}</>
        : <span className={d.projectType === "improvement" ? "a3-need" : "muted"}>{d.projectType === "improvement" ? "Not chosen: every improvement works on a process" : "None"}</span>}
      {editable && <button className="linkish" onClick={() => { setV({ process: p?.number ?? "", step: d.processStep?.id ?? "" }); setEdit(true); }}>{p ? "Change" : "Choose the process"}</button>}
    </div>
  );
}

function Box({ n, title, hint, children }: { n: number; title: string; hint?: string | false; children: ReactNode }) {
  return (
    <div className="a3-box">
      <h2><span className="a3-n">{n}</span>{title}</h2>
      {hint && <p className="a3-hint">{hint}</p>}
      {children}
    </div>
  );
}
const Para = ({ text }: { text: string }) => (text ? <p className="a3-text">{text}</p> : <p className="a3-text muted">Not written.</p>);

// ---------------------------------------------------------------------------------------------------------------
// Box 5: the tasks
// ---------------------------------------------------------------------------------------------------------------
function ToDo({ d, act }: { d: RequestDetail; act: Act }) {
  const [title, setTitle] = useState("");
  const [by, setBy] = useState<"person" | "ai">("person");
  const tasks = d.tasks.filter((x) => x.state !== "cancelled");
  const editable = ["define", "design"].includes(d.state);
  const add = async (e: FormEvent) => {
    e.preventDefault();
    if (title.trim().length < 3) return;
    if (await act(() => factory.addTasks(d.number, [{ title: title.trim(), doneBy: by }]))) setTitle("");
  };
  return (
    <>
      {tasks.length > 0 && <ul className="a3-tasks">{tasks.map((t) => <TaskRow key={t.id} t={t} act={act} canRemove={editable} />)}</ul>}
      {!tasks.length && !editable && <p className="a3-text muted">{d.state === "roadmap" ? "Start the request to plan the tasks." : "No tasks."}</p>}
      {editable && (
        <form className="a3-add" onSubmit={add}>
          <input className="input" aria-label="New task" placeholder="Add a task, e.g. Route supplier NCRs to engineering" value={title} onChange={(e) => setTitle(e.target.value)} />
          <select className="select" aria-label="Who does it" value={by} onChange={(e) => setBy(e.target.value as "person" | "ai")}><option value="person">A person</option><option value="ai">An AI tool</option></select>
          <button className="btn" disabled={title.trim().length < 3}>Add</button>
        </form>)}
    </>
  );
}

function TaskRow({ t, act, canRemove }: { t: Task; act: Act; canRemove: boolean }) {
  const done = ["approved", "deployed"].includes(t.state);
  const person = t.doneBy === "person";
  const canTick = person && ["ready", "in_progress"].includes(t.state);
  const who = t.claimedBy ? (t.claimedVia === "mcp" ? t.agent ?? "An AI tool" : t.claimedByName ?? "A person") : person ? "A person" : "An AI tool";
  let state: ReactNode = null;
  if (t.state === "draft") state = <span className="muted">After the plan is approved</span>;
  else if (t.state === "ready") state = <span className="muted">{person ? "To do" : "Ready for an AI tool"}</span>;
  else if (t.state === "in_progress") state = <span className="muted">{t.claimedVia === "mcp" ? `${who} is working on it` : `${who} is doing it`}</span>;
  else if (t.state === "review") state = <><span className="if-chip warn">Waiting for an OK{t.approvalsNeeded > 1 ? ` (${t.approvals.length} of ${t.approvalsNeeded})` : ""}</span>
    <button className="btn" onClick={() => act(() => factory.taskAct(t.number, "approve"), `OK given on ${t.number}.`)}>OK</button>
    <Link className="a3-more" to={`/factory/t/${t.number}`}>Check it first</Link></>;
  else if (t.state === "approved") state = <span className="muted">Done</span>;
  else if (t.state === "deployed") state = <span className="muted">Live</span>;
  return (
    <li className={`a3-task ${done ? "done" : ""}`}>
      {canTick ? <input type="checkbox" aria-label={`Done: ${t.title}`} checked={false} onChange={() => act(() => factory.taskAct(t.number, "done"), t.approvalsNeeded ? "Done. It waits for another person's OK." : "Done.")} />
        : <span className={`a3-tick ${done ? "on" : ""}`} aria-hidden="true">{done ? "✓" : ""}</span>}
      <span className="a3-task-t"><Link to={`/factory/t/${t.number}`}>{t.title}</Link><span className="sub">{who}{t.changeType !== "task" ? ` · ${t.changeTypeLabel.toLowerCase()}` : ""}</span></span>
      <span className="a3-task-s">{state}
        {canRemove && <button className="a3-x" aria-label={`Remove ${t.title}`} title="Remove from the plan" onClick={() => act(() => factory.taskAct(t.number, "cancel", { reason: "Removed from the plan" }))}>×</button>}
      </span>
    </li>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Box 6: the result, and keep or roll back
// ---------------------------------------------------------------------------------------------------------------
function Result({ d, act }: { d: RequestDetail; act: Act }) {
  const nav = useNavigate();
  const [value, setValue] = useState("");
  const live = d.deployments.find((x) => !x.rolledBackAt);
  const due = live ? new Date(new Date(live.deployedAt).getTime() + 30 * 864e5) : null;
  const readings = d.results.filter((r) => r.day !== 0);
  const goLive = d.results.find((r) => r.day === 0);
  const has30 = d.results.some((r) => r.day === 30);
  const n = value.trim() === "" ? null : Number(value);
  const record = async () => {
    if (n === null && !d.insight) return true;
    return act(() => factory.result(d.number, { day: has30 ? null : 30, value: n }));
  };
  const decide = async (decision: "keep" | "roll_back") => {
    let reason = "";
    if (decision === "roll_back") { const why = prompt("Why roll it back?"); if (!why || !why.trim()) return; reason = why.trim(); }
    if (!(await record())) return;
    await act(() => factory.decide(d.number, decision, reason), decision === "keep" ? (d.process && d.unit === "minutes" ? "Kept. The new minutes are in the process catalog, and the request is closed." : "Kept. The request is closed.") : "Rolled back. It's back at Plan.");
  };
  const list = (readings.length > 0 || goLive) && (
    <ul className="a3-readings">
      {goLive && <li><span>At go-live</span><b>{val(goLive.value, d.unit)}</b></li>}
      {readings.map((r) => <li key={r.id}><span>{r.day === null ? when(r.at) : `Day ${r.day}`}</span><b>{val(r.value, d.unit)}</b>{r.meetsTarget !== null && <span className={`if-chip ${r.meetsTarget ? "ok" : "warn"}`}>{r.meetsTarget ? "Target met" : "Not yet"}</span>}</li>)}
    </ul>);

  if (d.state === "measure") return (
    <>
      {list}
      <div className="a3-check">
        <label className="a3-f"><span>{has30 ? "Another reading" : "Result at 30 days"}{d.unit ? ` (${d.unit})` : ""}</span>
          <input className="input" type="number" step="any" value={value} onChange={(e) => setValue(e.target.value)} placeholder={d.insight ? "Empty: read process mining" : ""} /></label>
        <div className="btn-row">
          <button className="btn primary" disabled={!has30 && n === null && !d.insight} onClick={() => decide("keep")}>Keep</button>
          <button className="btn" onClick={() => decide("roll_back")}>Roll back</button>
          {n !== null && <button className="btn" onClick={() => record().then((ok) => ok && setValue(""))}>Save the reading only</button>}
        </div>
      </div>
      {due && <p className="a3-hint" style={{ marginTop: 8 }}>{Date.now() < due.getTime() ? `The 30-day result is due ${due.toLocaleDateString("en-US", { month: "short", day: "numeric" })}.` : "The 30-day result is due now."} Keep puts {d.process && d.unit === "minutes" ? "this number into the catalog as the new minutes each" : "the change into standard work"} and closes the request.</p>}
    </>
  );
  if (d.state === "recatalog") return <><>{list}</><Recatalog d={d} act={act} /></>;
  if (d.state === "closed") return (
    <>
      {list}
      <p className="a3-text"><b>{d.decision === "keep" ? "Kept." : "Closed."}</b>{d.recataloged ? <> {d.process?.number}: {d.recataloged.before?.minutesEach ?? "–"} → {d.recataloged.after.minutesEach ?? "–"} minutes each, {usd(d.recataloged.before?.costPerYear ?? 0)} → {usd(d.recataloged.after.costPerYear ?? 0)} a year{d.recataloged.systemsRetired.length ? `; retired ${d.recataloged.systemsRetired.join(", ")}` : ""}.</> : null}</p>
      {d.process && <button className="btn" onClick={() => nav(`/improve/catalog/${d.process!.number}`)}>Open {d.process.number} in the catalog</button>}
    </>
  );
  if (d.state === "cancelled") return list || <p className="a3-text muted">Cancelled.</p>;
  return list || null;
}

/** A request kept before Keep closed it: put the new minutes in the catalog and close it. */
function Recatalog({ d, act }: { d: RequestDetail; act: Act }) {
  const last = [...d.results].reverse().find((r) => r.day !== 0);
  const [m, setM] = useState(d.process && d.unit === "minutes" && last ? String(last.value) : "");
  const save = (e: FormEvent) => {
    e.preventDefault();
    act(() => factory.recatalog(d.number, d.process ? { minutesEach: m.trim() === "" ? null : Number(m), note: "Kept" } : { note: "Kept: no process numbers move" }), "Closed. The catalog has the new numbers.");
  };
  return (
    <form className="a3-check" onSubmit={save}>
      <p className="a3-text">Kept. {d.process ? `Put ${d.process.number}'s new minutes each in the catalog to close it.` : "Close it."}</p>
      {d.process && <label className="a3-f"><span>Minutes each (was {d.process.minutesEach ?? "–"})</span><input className="input" type="number" min={0} step="any" required value={m} onChange={(e) => setM(e.target.value)} /></label>}
      <button className="btn primary">Close</button>
    </form>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// History: who did what, folded away
// ---------------------------------------------------------------------------------------------------------------
function History({ d }: { d: RequestDetail }) {
  const rows = [
    ...d.history.map((h) => ({ at: h.at, text: h.reason && h.reason !== "created" ? h.reason : STATE_LABEL[h.toState] ?? h.toState, by: h.actorName })),
    ...d.deployments.map((x) => ({ at: x.deployedAt, text: `${x.number} live. To undo: ${x.rollbackPoint}${x.rolledBackAt ? ". Rolled back." : ""}`, by: null as string | null })),
  ].sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
  return (
    <details className="card a3-history">
      <summary><b>History</b> <span className="muted">{rows.length} entr{rows.length === 1 ? "y" : "ies"}</span></summary>
      <ul className="mx-hist">{rows.map((r, i) => <li key={i}><span className="when">{when(r.at)}</span><span>{r.text}{r.by && <span className="muted"> · {r.by}</span>}</span></li>)}</ul>
    </details>
  );
}
